import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import DarkModeToggle from 'front/components/ui/DarkModeToggle';
import { useTheme } from '../../../core/contexts/ThemeContext';
import apiClient from '../../../core/http/axios';
import ExpirationModal from 'front/components/ui/ExpirationModal';
import { validateName, validateEmail, formatEmailInput, getReadableErrorMessage } from '../../utils/formValidation';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background:
    url('/images/global/member_bg_bottom1.png') no-repeat left bottom,
    url('/images/global/member_bg_bottom2.png') no-repeat right bottom,
    url('/images/global/member_bg.png') no-repeat center center;
  background-size: auto, auto, cover;

  html.dark-mode & {
    background:
      url('/images/global/member_bg_bottom1_dark.png') no-repeat left bottom,
      url('/images/global/member_bg_bottom2_dark.png') no-repeat right bottom,
      url('/images/global/member_bg_dark.png') no-repeat center center;
  }
`;

const Header = styled.header`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl};
  & button {
    pointer-events: auto;
  }
`;

const Logo = styled.div`
  cursor: pointer;
  pointer-events: auto;

  html.dark-mode & img {
    filter: invert(1);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  gap: ${props => props.theme.spacing['4xl']};
  pointer-events: auto;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSize['2xl']};
  font-weight: ${props => props.theme.fontWeight.semibold};
  line-height: 141%;
  color: ${props => props.theme.colors.book.color1b};
  text-align: center;
`;

const Form = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  gap: ${props => props.theme.spacing.xl};
  pointer-events: auto;
`;

const FormField = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const LabelText = styled.label`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.book.color6b};
  & > .required {
    color: #DF0707;
    font-size: ${props => props.theme.fontSize.xs};
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing.sm};
  align-items: flex-start;
  width: 100%;
  &.row {
    flex-flow: row nowrap;
    align-items: center;
  }
`;

const Input = styled.input<{ $hasError: boolean }>`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.$hasError ? props.theme.colors.error[300] : props.theme.colors.white};
  background-color: ${props => props.theme.colors.white};
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.book.color2b};
  transition: all 0.2s;
  height: 44px;
  width: 100%;
  &:focus {
    outline: none;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px ${props => props.theme.colors.white} inset !important;
    -webkit-text-fill-color: ${props => props.theme.colors.book.color2b} !important;
    transition: background-color 5000s ease-in-out 0s;
  }

  &::placeholder {
    color: ${props => props.theme.colors.gray[400]};
  }

`;

const CodeButton = styled.button`
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.theme.colors.book.color3b};
  color: ${props => props.theme.colors.white};
  font-size: ${props => props.theme.fontSize.base};
  transition: all 0.2s;
  white-space: nowrap;
  height: 44px;
  width: 100%;

  &:disabled {
    opacity: 0.8;
    background-color: ${props => props.theme.colors.disabled};
    cursor: not-allowed;
  }
`;

const VerifyTimer = styled.div`
  position: absolute;
  right: 16px;
  top: 40px;
  color: ${props => props.theme.colors.primary[600]};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
  display: flex;
  align-items: center;
  justify-content: center;
`;


const ErrorMessage = styled.div`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.error[500]};
  margin-top: ${props => props.theme.spacing.xs};
`;

const ValidationMessage = styled.div<{ $isError: boolean }>`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.$isError ? props.theme.colors.error[500] : props.theme.colors.success[600]};
`;

const FindIdForm: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    code: '',
  });
  const [requestUuid, setRequestUuid] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [validation, setValidation] = useState({
    name: { isValid: false, message: '' },
    email: { isValid: false, message: '' },
  });

  const handleSendCode = async () => {
    if (!formData.name || !formData.email) {
      setError('이름과 이메일을 입력해주세요.');
      return;
    }

    const nameValidation = validateName(formData.name);
    if (!nameValidation.isValid) {
      setValidation(prev => ({ ...prev, name: nameValidation }));
      return;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      setValidation(prev => ({ ...prev, email: emailValidation }));
      return;
    }

    setIsSending(true);
    setError('');

    try {
      const response = await apiClient.post<{
        success: boolean;
        error: string | null;
        data: {
          request_uuid: string;
        };
        code: string | null;
        meta: {
          elapsed_ms: number;
        };
      }>('/auth/student/find-id/request', {
        name: formData.name,
        email: formData.email,
      });

      if (response.data.success && response.data.data?.request_uuid) {
        setRequestUuid(response.data.data.request_uuid);
        setCodeSent(true);
        setCountdown(180); // 3분
      } else {
        // success가 true지만 request_uuid가 없거나, error가 있는 경우
        const errorMsg = response.data.error || '인증코드 발송에 실패했습니다. 이메일 서버 설정을 확인해주세요.';
        setError(errorMsg);
        // console.error('인증코드 발송 실패:', response.data);
      }
    } catch (error: any) {
      const errorMessage = getReadableErrorMessage(error, '입력하신 이름 또는 이메일로 등록된 사용자를 찾을 수 없습니다.');
      setError(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!formData.code) {
      setError('인증코드를 입력해주세요.');
      return;
    }

    if (!requestUuid) {
      setError('인증코드를 먼저 발송해주세요.');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await apiClient.post<{
        success: boolean;
        error: string | null;
        data: {
          login_ids: string[];
        };
        code: string | null;
        meta: {
          elapsed_ms: number;
        };
      }>('/auth/student/find-id/verify', {
        request_uuid: requestUuid,
        code: formData.code,
      });

      if (response.data.success && response.data.data?.login_ids) {
        navigate('/find-id/result', {
          state: { loginIds: response.data.data.login_ids },
        });
      } else {
        const msg = response.data.error;
        setError(typeof msg === 'string' ? msg : '인증코드가 올바르지 않습니다.');
      }
    } catch (error: any) {
      const errorMessage = getReadableErrorMessage(error, '인증코드 확인 중 오류가 발생했습니다.');
      setError(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  // 카운트다운 관리
  useEffect(() => {
    if (codeSent && countdown > 0 && !codeVerified) {
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // 만료 처리
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
              countdownTimerRef.current = null;
            }
            setShowExpirationModal(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [codeSent, countdown, codeVerified]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResendCode = async () => {
    setShowExpirationModal(false);
    setCodeSent(false);
    setCountdown(0);
    setFormData(prev => ({ ...prev, code: '' }));
    setRequestUuid('');
    await handleSendCode();
  };

  return (
    <Container>
      <Header>
        <Logo onClick={() => navigate('/')}><img src="/images/global/logo.svg" alt="logo" /></Logo>
        <DarkModeToggle isDark={isDarkMode} onToggle={toggleDarkMode} />
      </Header>
      <Content>
        <Title>아이디 찾기</Title>
        <Form onSubmit={(e) => e.preventDefault()}>
          <FormField>
            <LabelText htmlFor="name">
              이름 <span className="required">*</span>
            </LabelText>
            <Input
              type="text"
              placeholder="이름을 입력해주세요."
              value={formData.name}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({ ...prev, name: value }));
                setValidation(prev => ({ ...prev, name: value ? validateName(value) : { isValid: false, message: '' } }));
                setError('');
              }}
              $hasError={!!formData.name && !validation.name.isValid && !!validation.name.message}
            />
            {formData.name && validation.name.message && (
              <ValidationMessage $isError={!validation.name.isValid}>
                {validation.name.message}
              </ValidationMessage>
            )}
          </FormField>

          <FormField>
            <LabelText htmlFor="email">
              이메일 인증 <span className="required">*</span>
            </LabelText>
            <InputGroup>
              <Input
                type="email"
                placeholder="회원가입시 사용한 이메일을 입력해주세요"
                value={formData.email}
                onChange={(e) => {
                  const value = formatEmailInput(e.target.value);
                  setFormData(prev => ({ ...prev, email: value }));
                  setValidation(prev => ({ ...prev, email: value ? validateEmail(value) : { isValid: false, message: '' } }));
                  setError('');
                }}
                $hasError={!!formData.email && !validation.email.isValid && !!validation.email.message}
              />
            </InputGroup>
            {formData.email && validation.email.message && !validation.email.isValid && (
              <ValidationMessage $isError={true}>
                {validation.email.message}
              </ValidationMessage>
            )}
          </FormField>
          <FormField>
            {codeSent && (
              <InputGroup style={{ position: 'relative' }}>
                <LabelText htmlFor="email">
                  인증코드 입력 <span className="required">*</span>
                </LabelText>
                <Input
                  type="text"
                  placeholder="인증코드를 입력해주세요"
                  value={formData.code}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, code: e.target.value }));
                    setError('');
                  }}
                  $hasError={!!error}
                />
                <VerifyTimer>{formatTime(countdown)}</VerifyTimer>
              </InputGroup>
            )}
          </FormField>

          {!codeSent && (
          <CodeButton
            type="button"
            onClick={handleSendCode}
            disabled={countdown > 0 || isSending}
          >
            {isSending ? '발송 중...' : countdown > 0 ? `다시보내기` : '인증코드'}
          </CodeButton>
          )}
          {codeSent && (
            <CodeButton
              type="button"
              onClick={handleVerifyCode}
              disabled={!formData.code || isVerifying}
            >
              {isVerifying ? '확인 중...' : '인증 하기'}
            </CodeButton>
          )}
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Form>
      </Content>
      <ExpirationModal
        isOpen={showExpirationModal}
        onConfirm={handleResendCode}
        onCancel={() => setShowExpirationModal(false)}
        title="인증 시간 만료"
        message="인증 유효 시간이 만료되었습니다.&#10;다시 인증 코드를 받아주세요."
        confirmText="다시 받기"
        cancelText="취소"
      />
    </Container>
  );
};

export default FindIdForm;
